/**
 * ---------------------------------------------------------------------------------------------------------------
 * BIRTHSTONE / METAOBJECT PICKER
 * ---------------------------------------------------------------------------------------------------------------
 *
 * Turns metaobject-driven option grids into line item properties. It is fully independent from the native
 * <variant-picker>: it never touches variant ids, it only writes `properties[...]` inputs on the product form.
 *
 * Emitted event: `birthstone:change` (bubbles) with { field, property, value, image }.
 */
(() => {
  if (window.customElements.get('birthstone-picker')) return;

  const FIELD = '[data-birthstone-field]';

  /** Escapes a value so it can be used inside a quoted attribute selector. */
  const attr = (value) => String(value).replace(/(["\\])/g, '\\$1');

  /**
   * A drawer teleports itself to <body> when it opens and — because the theme clears its own
   * `_originalParentBeforeAppend` on every open — it stays there. Grids and fields therefore stop being
   * ancestors/descendants of each other, so the two are wired to each other by direct reference at init time
   * rather than looked up by id. Element references survive any amount of moving around; document queries on a
   * shared id do not.
   */
  const link = (picker) => {
    picker.querySelectorAll(FIELD).forEach((field) => {
      const id = field.getAttribute('data-field-id');
      const grid =
        field.querySelector('[data-birthstone-grid]') ||
        document.querySelector(`[data-birthstone-grid][data-field="${attr(id)}"]`);

      if (!grid) return;

      field._bsGrid = grid;
      field._bsPicker = picker;
      field._bsDrawer = grid.closest('x-drawer') || field._bsDrawer || null;
      grid._bsField = field;
    });
  };

  /** Returns the field element a grid (which may have been teleported into a drawer on <body>) belongs to. */
  const fieldForGrid = (grid) => grid._bsField || null;

  /**
   * Selections survive a reload. sessionStorage (not localStorage) so the choice lives as long as the visit and
   * does not resurface days later on a product the customer has moved on from.
   */
  const storageKey = (picker, field) => {
    const product = picker.getAttribute('data-product-id') || '';
    const property = field.getAttribute('data-property') || field.getAttribute('data-field-id') || '';
    return `birthstone-picker:${product}:${property}`;
  };

  const remember = (picker, field, value, image) => {
    try {
      const key = storageKey(picker, field);
      if (value) {
        sessionStorage.setItem(key, JSON.stringify({ value, image }));
      } else {
        sessionStorage.removeItem(key);
      }
    } catch (error) {
      // Private browsing, or storage disabled — the picker still works, it just will not survive a reload.
    }
  };

  const recall = (picker, field) => {
    try {
      const stored = sessionStorage.getItem(storageKey(picker, field));
      return stored ? JSON.parse(stored) : null;
    } catch (error) {
      return null;
    }
  };

  const setError = (field, hasError) => {
    field.classList.toggle('has-error', hasError);
    const error = field.querySelector('[data-birthstone-error]');
    if (error) error.hidden = !hasError;
  };

  /** The option grid for a field — it may have been teleported into a drawer on <body>. */
  const gridForField = (field) => field._bsGrid || null;

  /**
   * Single place that writes a field's state: the hidden inputs, the trigger label, the thumbnail, the
   * has-value flag and the stored value. Passing an empty value clears the field.
   */
  const apply = (field, value, image, { closeDrawer = true, persist = true } = {}) => {
    const picker = field.closest('birthstone-picker');
    const grid = gridForField(field);
    const hasValue = value !== '';

    if (grid) {
      grid.querySelectorAll('[data-birthstone-option]').forEach((element) => {
        element.setAttribute('aria-checked', String(hasValue && element.getAttribute('data-value') === value));
      });
    }

    const input = field.querySelector('[data-birthstone-input]');
    if (input) {
      input.value = value;
      input.disabled = !hasValue;
    }

    const imageInput = field.querySelector('[data-birthstone-image-input]');
    if (imageInput) {
      imageInput.value = image;
      imageInput.disabled = image === '';
    }

    const valueLabel = field.querySelector('[data-birthstone-value]');
    if (valueLabel) {
      valueLabel.textContent = value || valueLabel.getAttribute('data-placeholder') || '';
      valueLabel.classList.toggle('is-placeholder', !hasValue);
    }

    const thumb = field.querySelector('[data-birthstone-thumb]');
    if (thumb) {
      thumb.hidden = image === '';
      thumb.innerHTML = image ? `<img src="${image}" alt="" width="28" height="28" loading="lazy">` : '';
    }

    field.classList.toggle('has-value', hasValue);
    setError(field, false);

    if (persist && picker) remember(picker, field, value, image);

    field.dispatchEvent(
      new CustomEvent('birthstone:change', {
        bubbles: true,
        detail: { field, property: input ? input.name : null, value, image }
      })
    );

    if (closeDrawer) {
      const drawer = field._bsDrawer || (grid && grid.closest('x-drawer'));
      if (drawer && typeof drawer.hide === 'function') drawer.hide();
    }
  };

  /**
   * The theme reveals a drawer by animating `visibility` and the shadow content's `clip-path` with Motion. When
   * that animation never runs — the tab was backgrounded, or the main thread was busy during the first open —
   * the drawer stays flagged `open` while painted closed: invisible and click-through, because `clip-path`
   * clips hit-testing too. CSS restores `visibility`; the clip-path lives on inline style inside the shadow
   * root, so it has to be snapped here. Runs only when the animation genuinely failed to land.
   */
  const OPEN_ANIMATION_MS = 700;

  const healOpenDrawer = (drawer) => {
    if (!drawer || !drawer.hasAttribute('open')) return;

    const content = drawer.shadowRoot && drawer.shadowRoot.querySelector('[part="content"]');
    if (!content) return;

    // Still clipped this long after opening means the reveal animation never landed.
    const clip = getComputedStyle(content).clipPath;
    if (!clip || !clip.includes('100%')) return;

    // The animation is WAAPI, and the animation origin outranks inline styles — writing `style.clipPath` here
    // would simply be ignored. Finishing the animations jumps them to the very end state we want instead.
    [drawer, content, ...Array.from(content.children)].forEach((element) => {
      element.getAnimations().forEach((animation) => {
        try {
          animation.finish();
        } catch (error) {
          // Infinite or unresolved animations cannot be finished; nothing else to do for them.
        }
      });
    });

    // Belt and braces, in case the drawer was opened without any animation being created at all.
    if (getComputedStyle(content).clipPath.includes('100%')) {
      content.style.clipPath = 'inset(0 0 0 0 round var(--rounded-sm))';
    }
    if (getComputedStyle(content).opacity === '0') content.style.opacity = '1';
    Array.from(content.children).forEach((child) => {
      if (getComputedStyle(child).opacity === '0') child.style.opacity = '1';
    });
  };

  const watchDrawer = (drawer) => {
    if (!drawer || drawer._bsWatched) return;
    drawer._bsWatched = true;

    drawer.addEventListener('toggle', () => {
      clearTimeout(drawer._bsHealTimer);
      if (!drawer.hasAttribute('open')) return;
      drawer._bsHealTimer = setTimeout(() => healOpenDrawer(drawer), OPEN_ANIMATION_MS);
    });
  };

  const select = (grid, option) => {
    const field = fieldForGrid(grid);
    if (!field) return;

    apply(field, option.getAttribute('data-value') || '', option.getAttribute('data-image') || '');
  };

  class BirthstonePicker extends HTMLElement {
    connectedCallback() {
      this._onClick = this._onClick.bind(this);
      document.addEventListener('click', this._onClick);

      // Reflect the placeholder state on load.
      this.querySelectorAll('[data-birthstone-value]').forEach((element) => {
        const placeholder = element.getAttribute('data-placeholder') || '';
        element.classList.toggle('is-placeholder', element.textContent.trim() === placeholder.trim());
      });

      link(this);
      this.fields.forEach((field) => watchDrawer(field._bsDrawer));

      this._restore();

      this._form = document.forms[this.getAttribute('form')];
      if (this._form && !this._form.dataset.birthstoneValidated) {
        this._form.dataset.birthstoneValidated = 'true';
      }
    }

    disconnectedCallback() {
      document.removeEventListener('click', this._onClick);

      // A drawer that had been teleported to <body> is not removed with the picker, so it would linger as an
      // orphan — invisible, still holding this product's ids, and able to shadow the replacement picker.
      this.fields.forEach((field) => {
        const drawer = field._bsDrawer;
        if (drawer && drawer.parentElement === document.body) {
          clearTimeout(drawer._bsHealTimer);
          if (typeof drawer.hide === 'function' && drawer.hasAttribute('open')) drawer.removeAttribute('open');
          drawer.remove();
        }
      });
    }

    /** All fields belonging to this picker, including grids teleported into a drawer on <body>. */
    get fields() {
      return Array.from(this.querySelectorAll(FIELD));
    }

    /**
     * Re-applies the choices kept from before a reload. A stored value is only restored when it still exists in
     * the option list, so editing the metaobjects cannot leave a customer with a stone that is no longer offered.
     */
    _restore() {
      this.fields.forEach((field) => {
        const stored = recall(this, field);
        if (!stored || !stored.value) return;

        const grid = gridForField(field);
        const option =
          grid && grid.querySelector(`[data-birthstone-option][data-value="${attr(stored.value)}"]:not([disabled])`);

        if (!option) {
          remember(this, field, '', '');
          return;
        }

        apply(field, option.getAttribute('data-value') || '', option.getAttribute('data-image') || '', {
          closeDrawer: false
        });
      });
    }

    _onClick(event) {
      const clear = event.target.closest('[data-birthstone-clear]');
      if (clear) {
        const field = clear.closest(FIELD);
        if (field && this.contains(field)) {
          event.preventDefault();
          event.stopPropagation();
          apply(field, '', '');
        }
        return;
      }

      const option = event.target.closest('[data-birthstone-option]');
      if (!option || option.disabled) return;

      const grid = option.closest('[data-birthstone-grid]');
      if (!grid) return;

      const field = fieldForGrid(grid);
      if (!field || !this.contains(field)) return;

      event.preventDefault();
      select(grid, option);
    }

    /** Returns true when every required selector has a value; otherwise flags the offenders. */
    validate() {
      let firstInvalid = null;

      this.fields.forEach((field) => {
        const required = field.getAttribute('data-required') === 'true';
        const input = field.querySelector('[data-birthstone-input]');
        const invalid = required && (!input || input.disabled || input.value.trim() === '');

        setError(field, invalid);
        if (invalid && !firstInvalid) firstInvalid = field;
      });

      if (firstInvalid) {
        firstInvalid.scrollIntoView({ behavior: 'smooth', block: 'center' });
        const trigger = firstInvalid.querySelector('[data-birthstone-trigger]');
        if (trigger) trigger.focus({ preventScroll: true });
        return false;
      }

      return true;
    }
  }

  window.customElements.define('birthstone-picker', BirthstonePicker);

  /**
   * Every picker attached to a given product form.
   * `form.getAttribute('id')` — not `form.id` — because the product form contains an input named `id`
   * (the variant id), and named controls shadow the form's own properties.
   */
  const pickersFor = (form) => {
    const id = form && form.getAttribute('id');
    return id ? Array.from(document.querySelectorAll(`birthstone-picker[form="${attr(id)}"]`)) : [];
  };

  const blockIfInvalid = (form, event) => {
    const pickers = pickersFor(form);
    if (pickers.length === 0) return;

    // `every` short-circuits, and we want every field flagged, so validate them all first.
    const results = pickers.map((picker) => picker.validate());
    if (results.includes(false)) {
      event.preventDefault();
      event.stopPropagation();
      if (typeof event.stopImmediatePropagation === 'function') event.stopImmediatePropagation();
    }
  };

  /**
   * Block the submit before the theme's <form is="product-form"> handler runs. Listeners registered on the form
   * itself all fire during the AT_TARGET phase in registration order, so the only reliable way to run first is a
   * capturing listener higher up the tree.
   */
  document.addEventListener(
    'submit',
    (event) => {
      if (event.target instanceof HTMLFormElement) blockIfInvalid(event.target, event);
    },
    true
  );

  /**
   * Cart apps (iCart, upsell drawers, ...) usually hijack the click on the Add to cart button and post to
   * /cart/add.js themselves, so the submit event above never fires. Catching the click during the capture phase
   * runs before any of those handlers, whatever order they were registered in.
   */
  document.addEventListener(
    'click',
    (event) => {
      const button = event.target.closest('button, input[type="submit"], [role="button"]');
      if (!button) return;

      const type = (button.getAttribute('type') || '').toLowerCase();
      const isSubmit = type === 'submit' || button.name === 'add' || button.hasAttribute('data-add-to-cart');
      if (!isSubmit) return;

      const form = button.form || (button.getAttribute('form') && document.getElementById(button.getAttribute('form')));
      if (!(form instanceof HTMLFormElement)) return;

      blockIfInvalid(form, event);
    },
    true
  );
})();
